import { useTranslation } from 'react-i18next';
import { LegRows } from './leg-rows';
import { GLOBAL_EVENT, pick, type AccountOption, type Lang, type PostingCatalog, type RoleCoverage } from './model';

interface DefaultsTabProps {
  catalog: PostingCatalog;
  lang: Lang;
  globals: RoleCoverage[];
  accountOptions: AccountOption[];
  canEdit: boolean;
  busyKey: string | null;
  onSave: (role: string, dimension: string, accountId: string) => void;
  onRemove: (ruleId: string) => void;
}

/**
 * Global defaults: one routing table per role, applying to every event that
 * has no event-specific rule for the same slot.
 */
export function DefaultsTab({ catalog, lang, globals, accountOptions, canEdit, busyKey, onSave, onRemove }: DefaultsTabProps) {
  const { t } = useTranslation('postingRules');
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('defaults.intro')}</p>
      {globals.map((role) => (
        <div key={role.role} className="border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
            <span className="font-medium">{pick(role.label, lang)}</span>
            <code className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{role.role}</code>
            {role.expectedPrefixes.length > 0 && (
              <span className="text-xs text-muted-foreground">{t('defaults.expectedPrefixes', { prefixes: role.expectedPrefixes.join(', ') })}</span>
            )}
          </div>
          <p className="px-3 pt-2 text-xs text-muted-foreground">{pick(role.description, lang)}</p>
          <LegRows
            catalog={catalog}
            lang={lang}
            event={GLOBAL_EVENT}
            role={role.role}
            dimensions={role.dimensions}
            rows={role.rows}
            accountOptions={accountOptions}
            canEdit={canEdit}
            busyKey={busyKey}
            onSave={(dimension, accountId) => onSave(role.role, dimension, accountId)}
            onRemove={onRemove}
          />
        </div>
      ))}
    </div>
  );
}
